import { fetcher } from '@commenda/utils';
import { AM_TP_DEMO_ROUTES } from './types';

const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Downloads one of the demo's own documents, if the demo holds it.
 *
 * **Not the product's file service.** `downloadFileFromFileId` asks `s3/signedUrlForGet` to sign `File.fileUrl` as
 * an S3 URI, so the row would have to point at S3 — which these documents deliberately do not. They are streamed
 * from the backend instead, and reaching that route means a fetch that carries the caller's token, which a plain
 * anchor download cannot do.
 *
 * Returns `false` when the demo holds no bytes for the file, which is most of them: the caller then falls back to
 * the product's own download and gets whatever `File.fileUrl` points at. So a caller needs no knowledge of which
 * documents are real — it asks, and falls back.
 *
 * Addressed by the name the caller is already showing, which is also the name the download is saved under.
 */
export async function downloadAmTpDemoAsset(
  target: { fileName: string }
): Promise<boolean> {
  try {
    const response = await fetcher(
      `${AM_TP_DEMO_ROUTES.asset}?fileName=${encodeURIComponent(target.fileName)}`,
      { method: 'GET' }
    );

    if (!response.ok) return false;

    const blob = await response.blob();

    // An empty body is a failure the response status did not report — downloading it would save a 0-byte document
    // over the presenter's shoulder.
    if (blob.size === 0) return false;

    // `Content-Disposition` is not exposed to `fetch` across origins, so the download is saved under the name the
    // caller was already showing — which is the name the route resolved the bytes by.
    triggerBlobDownload(blob, target.fileName);

    return true;
  } catch {
    return false;
  }
}
